import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Clock, AlertTriangle, FileX } from 'lucide-react';

export const ProblemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const problems = [
    {
      icon: Clock,
      title: 'Hours of Manual Work',
      description: 'Teams spend countless hours copying data from invoices and receipts into spreadsheets.',
    },
    {
      icon: AlertTriangle,
      title: 'Human Errors',
      description: 'Manual data entry leads to typos, missed fields, and costly mistakes in financial records.',
    },
    {
      icon: FileX,
      title: 'Inconsistent Formats',
      description: 'Different document layouts require custom handling, slowing down processing.',
    },
  ];

  return (
    <section
      ref={ref}
      className="py-20 md:py-28 bg-[#F7F8FC]"
      data-testid="problem-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-red-50 text-red-600 text-sm font-medium rounded-full mb-4">
            The Problem
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A2540] mb-6">
            Manual Document Processing is{' '}
            <span className="text-red-500">Broken</span>
          </h2>
          <p className="text-lg text-[#425466] leading-relaxed">
            Businesses waste valuable time and resources manually extracting data from documents like invoices, receipts, and purchase orders.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {problems.map((problem, index) => (
            <motion.div
              key={problem.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300"
            >
              <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mb-6">
                <problem.icon className="w-7 h-7 text-red-500" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#0A2540] mb-3">
                {problem.title}
              </h3>
              <p className="text-[#425466] leading-relaxed">
                {problem.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <p className="text-lg text-[#425466]">
            <span className="font-semibold text-[#0A2540]">SmartOCR</span> automates this entire process with AI.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
